import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { observable } from "mobx";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import { signalsData } from "Stores/signalsData";
import { toasterState } from "Components/common/toaster";
import { makeApi, URLS } from "Utils/apiURL";
import AgentSelect from "../agents/AgentSelect";
import { defaultTimezone } from "../agents/DayOfWeekField";
import SignalIcon from "./SignalIcon";
import { familyOfType } from "./signalMeta";
import {
  STEPS, TELL, GROUPS, entityOf, signalNameOf, isMove, defaultSteps, stateAt, validOrder, insertAt, offeredFor,
  addReason, suggestionsFor, lockNote, costLine, missingPick, buildAgent
} from "./automateFlow";

/** Which signal the drawer is open on ({row, listId}); any Signals page can open it, the page's host draws it. */
export const automateState = observable({ open: null });
export const openAutomate = async (row, listId = "") => {
  if (!row) return;
  // Company or contact decides the starting steps, and the catalog says which: load it first if it isn't in yet.
  if (!signalsData.catalog.length) await signalsData.fetchCatalog();
  automateState.open = { row, listId };
};

let uid = 0;
const escapeHtml = s => String(s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const hashed = name => (String(name || "").startsWith("#") ? name : `#${name}`);

/** A step's starting settings, filled from where the drawer was opened. */
const paramsFor = (key, ctx) => {
  const p = { ...STEPS[key].params };
  if (key === "act.add_to_account_list") p.listName = `${ctx.signal} accounts`;
  if (key === "act.add_to_list") Object.assign(p, ctx.listId ? { listId: ctx.listId } : { newListName: `${ctx.signal} contacts` });
  if (key === "act.update_contact") Object.assign(p, { addTag: ctx.signal }, ctx.move ? { applyMove: "true" } : {});
  if (key === "act.create_task") p.note = `Follow up: ${ctx.signal} found a move`;
  if (key === "act.add_note") p.note = `Flagged by your ${ctx.signal} agent`;
  if (key === TELL) p.mode = "email";
  if (key === "filter.icp_match" && agentsData.savedSearches.length) p.savedSearchId = agentsData.savedSearches[0].id;
  if (key === "act.export_to_crm") p.crm = (agentsData.crms.find(c => c.connected) || {}).value;
  return p;
};
const stepOf = (key, ctx) => ({ id: ++uid, key, params: paramsFor(key, ctx) });

const choice = (value, options, apply) => ({ value, options, apply });
const counted = (list, label) => list.map(n => ({ value: String(n), label: label(n) }));

/** The one setting a step card shows: its picker value, the options and how a pick changes the step's params. */
const settingOf = (step, ctx) => {
  const p = step.params;
  switch (step.key) {
    case "expand.people_at_company":
      return choice(String(p.perCompany), counted([3, 5, 10], n => `${n} decision-makers per company`), v => ({ perCompany: Number(v) }));
    case "expand.find_alternate_contact":
      return choice(String(p.perContact), counted([1, 2, 3], n => (n === 1 ? "1 person" : `${n} people`)), v => ({ perContact: Number(v) }));
    case "enrich.find_email":
      return choice(p.findPhone, [{ value: "no", label: "Email only" }, { value: "yes", label: "Email and phone" }, { value: "only", label: "Phone only" }], v => ({ findPhone: v }));
    case "enrich.research":
      return choice(String(p.freshnessDays), counted([7, 30, 90], n => `Reuse research up to ${n} days old`), v => ({ freshnessDays: Number(v) }));
    case "filter.icp_match":
      return choice(p.savedSearchId, agentsData.savedSearches.map(s => ({ value: s.id, label: s.label })), v => ({ savedSearchId: v }));
    case "act.add_to_account_list":
      return choice(p.accountListId || "new", [{ value: "new", label: `${ctx.signal} accounts (new)` }, ...agentsData.accountLists.map(l => ({ value: l.id, label: l.name }))],
        v => (v === "new" ? { listName: `${ctx.signal} accounts` } : { accountListId: v }));
    case "act.add_to_list":
      return choice(p.listId || "new", [{ value: "new", label: `${ctx.signal} contacts (new)` }, ...agentsData.contactLists.map(l => ({ value: l.id, label: l.name }))],
        v => (v === "new" ? { newListName: `${ctx.signal} contacts` } : { listId: v }));
    case "act.enroll_sequence":
      return choice(p.sequenceId, agentsData.sequencesLite, v => ({ sequenceId: v }));
    case "act.stop_sequence":
      return choice(p.mode, [{ value: "stop", label: "Stop any sequence they are in" }, { value: "pause", label: "Pause any sequence they are in" }], v => ({ mode: v }));
    case "act.update_contact":
      // A person who moved: write the new title and company the signal found (and tag them), or just tag / set a stage.
      return choice(p.applyMove === "true" ? "move" : p.addTag ? `tag:${p.addTag}` : `stage:${p.stage}`,
        [...(ctx.move ? [{ value: "move", label: "New title and company" }] : []),
          { value: `tag:${ctx.signal}`, label: `Tag them: ${ctx.signal}` }, ...agentsData.contactStages.map(s => ({ value: `stage:${s.value}`, label: `Set stage: ${s.label}` }))],
        v => (v === "move" ? { applyMove: "true", addTag: ctx.signal } : { applyMove: undefined, ...(v.startsWith("tag:") ? { addTag: v.slice(4) } : { stage: v.slice(6) }) }));
    case "act.update_account":
      return choice(p.stage, ctx.accountStages.map(s => ({ value: s.name, label: s.name })), v => ({ stage: v }));
    case "act.export_to_crm":
      return choice(p.crm, agentsData.crms.filter(c => c.connected).map(c => ({ value: c.value, label: c.label })), v => ({ crm: v }));
    case "act.create_task":
      return choice(String(p.dueInDays), [{ value: "0", label: "Due today" }, { value: "1", label: "Due tomorrow" }, { value: "3", label: "Due in 3 days" }], v => ({ dueInDays: Number(v) }));
    case "wait.delay":
      return choice(String(p.amount), counted([1, 3, 7, 14], n => (n === 1 ? "1 day" : `${n} days`)), v => ({ amount: Number(v), unit: "days" }));
    case TELL: {
      const channels = agentsData.slackChannels || [];
      return choice(`${p.mode}:${p.channel || ""}`, [
        { value: "email:", label: "Email · right away" },
        { value: "digest:", label: "Email · daily digest" },
        ...channels.map(c => ({ value: `slack:${c.id}`, label: `Slack · ${hashed(c.name)}` })),
        ...channels.map(c => ({ value: `both:${c.id}`, label: `Email and Slack · ${hashed(c.name)}` }))
      ], v => { const [mode, channel] = v.split(":"); return { mode, channel: channel || undefined }; });
    }
    default:
      return null;
  }
};

/**
 * "New agent for <signal>": what happens, step by step, each time a tracked signal finds a move. Opens with the
 * steps that fit the signal already in place (a company signal lists the company; a person who moved is stopped,
 * tagged and listed), so Turn on is the only click needed. Steps come from the agent engine's own catalog.
 */
const SignalAutomateDrawer = ({ row, listId = "", onClose, onChange = () => {} }) => {
  const history = useHistory();
  const entity = entityOf(row, signalsData.catalog);
  const signal = signalNameOf(row, signalsData.catalog);
  const move = isMove(row);
  const fam = familyOfType(signalsData.catalog, (row.signal || {}).signalType);
  const [accountStages, setAccountStages] = useState([]);
  const ctx = { signal, entity, move, listId, accountStages };
  const [steps, setSteps] = useState(() => defaultSteps(entity, move).map(k => stepOf(k, ctx)));
  const [catalogTypes, setCatalogTypes] = useState(null);
  const [skipSeen, setSkipSeen] = useState(true);
  const [skipInSequence, setSkipInSequence] = useState(true);
  const [search, setSearch] = useState(null); // null: closed; else {query, group}
  const [drag, setDrag] = useState(null);
  const [busy, setBusy] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    agentsData.fetchStepTypes().then(list => list.length && setCatalogTypes(list.map(t => t.type)));
    agentsData.fetchSlackChannels();
    agentsData.fetchSequencesLite();
    agentsData.fetchContactLists();
    agentsData.fetchAccountLists();
    agentsData.fetchContactStages();
    agentsData.fetchSavedSearches();
    agentsData.fetchCrms();
    makeApi({ url: URLS.getAccountStages, method: "GET" }).then(res => Array.isArray(res && res.data) && setAccountStages(res.data));
  }, []);
  useEffect(() => {
    // Escape closes an open picker first (its own outside-click close), and only then the drawer, so steps aren't lost.
    const onKey = e => {
      if (e.key !== "Escape") return;
      const menu = document.querySelector(".sg-auto .ag-select__menu");
      if (menu) document.querySelector(".sg-auto__head").dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
      else onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Opening search brings the whole list into view, not just its first row above the footer.
  useEffect(() => {
    const input = searchRef.current;
    if (!search || !input) return;
    input.focus({ preventScroll: true });
    const card = input.closest(".sg-auto__card--add");
    if (card && card.scrollIntoView) card.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [!!search]); // eslint-disable-line

  const keys = steps.map(s => s.key);
  const slackOff = agentsData.slackChannelsLoaded && !(agentsData.slackChannels || []).length;
  // Steps that need something set up elsewhere first.
  const missing = {
    ...(agentsData.savedSearches.length ? {} : { "filter.icp_match": "Needs a saved List Builder search" }),
    ...(agentsData.crms.some(c => c.connected) ? {} : { "act.export_to_crm": "Connect a CRM first" })
  };
  const offered = offeredFor(entity, catalogTypes);
  const suggestions = suggestionsFor(keys, entity, catalogTypes, missing);
  const locked = lockNote(keys, entity);
  const compact = steps.length >= 5;
  const gap = missingPick(steps);
  const nameAt = (key, i) => STEPS[key].name({ ...ctx, ...stateAt(keys, i, entity) });

  const add = key => {
    const at = insertAt(keys);
    setSteps([...steps.slice(0, at), stepOf(key, ctx), ...steps.slice(at)]);
    setSearch(null);
  };
  const remove = i => setSteps(steps.filter((_, j) => j !== i));
  const canRemove = i => validOrder(keys.filter((_, j) => j !== i), entity);
  const setParams = (i, patch) => setSteps(steps.map((s, j) => {
    if (j !== i) return s;
    const params = { ...s.params, ...patch };
    // A new list replaces a picked one (and back), so only one of them is ever sent.
    if (patch.newListName) delete params.listId;
    if (patch.listId) delete params.newListName;
    if (patch.listName) delete params.accountListId;
    if (patch.accountListId) delete params.listName;
    if (patch.addTag) delete params.stage;
    if (patch.stage && s.key === "act.update_contact") delete params.addTag;
    return { ...s, params };
  }));
  // Moves step `from` to `to` unless that would put a step above what it needs.
  const move_ = (from, to) => {
    if (to < 0 || to >= steps.length || from === to) return;
    const next = steps.slice();
    next.splice(to, 0, next.splice(from, 1)[0]);
    if (validOrder(next.map(s => s.key), entity)) setSteps(next);
    else toasterState.setToastMsg(to < from
      ? `${nameAt(steps[from].key, from)} has to stay below the step it needs.`
      : `A step below needs ${nameAt(steps[from].key, from)}, so it stays above it.`, "warning");
  };

  const openEditor = () => {
    agentsData.stageDraft({ ...buildAgent({ row, name: `${signal} agent`, steps, skipSeen, skipInSequence, timezone: defaultTimezone() }), status: "DRAFT" });
    onClose();
    history.push("/agents/new/custom");
  };

  const turnOn = async () => {
    if (busy || gap) return;
    setBusy(true);
    // A new contact list is made now (the engine needs its id); a new account list is made by the agent itself.
    let ready = steps;
    const listStep = steps.find(s => s.key === "act.add_to_list" && s.params.newListName);
    if (listStep) {
      const name = listStep.params.newListName;
      const made = await agentsData.createContactList(name);
      let id = made.list && made.list.id;
      if (!id) id = ((await agentsData.fetchContactLists()) || []).find(l => l.name === name)?.id;
      if (!id) {
        setBusy(false);
        toasterState.setToastMsg(made.error || "Couldn't make the list. Try again.", "failure");
        return;
      }
      ready = steps.map(s => (s === listStep ? { ...s, params: { ...s.params, listId: id, newListName: undefined } } : s));
    }
    const name = `${signal} agent`;
    const agent = await agentsData.createAgent(buildAgent({ row, name, steps: ready, skipSeen, skipInSequence, timezone: defaultTimezone() }));
    setBusy(false);
    if (!agent || !agent.id) return; // createAgent already said why (e.g. the team's agent limit)
    onClose();
    signalsData.fetchSaved();
    onChange();
    toasterState.setToasterTimeout(8000);
    toasterState.setPopupValues({
      showUndoButton: true, custom: true, link: `/agents/${agent.id}`,
      callback: async () => { if (await agentsData.deleteAgent(agent.id)) { signalsData.fetchSaved(); onChange(); toasterState.setToastMsg("Agent removed", "success"); } }
    });
    toasterState.setToastMsg(`${escapeHtml(name)} is on. It runs on the next move. <span class="showRevealedContacts">View agent</span>`, "success");
  };

  const pickFor = (step, i) => {
    const set = settingOf(step, ctx);
    if (!set) return <span className="sg-auto__static">{STEPS[step.key].note(ctx)}</span>;
    return (
      <div className="sg-auto__set">
        <AgentSelect value={set.value} options={set.options} searchable={set.options.length > 8} placeholder="Choose…" onChange={v => setParams(i, set.apply(v))} />
      </div>
    );
  };

  const shownSearch = search && offered
    .filter(k => search.group === "all" || STEPS[k].group === search.group)
    .filter(k => !search.query.trim() || nameAt(k, insertAt(keys)).toLowerCase().includes(search.query.trim().toLowerCase()));

  return ReactDOM.createPortal(
    <div className="sg-drawer-bg" onClick={onClose}>
      <aside className="sg-drawer sg-auto" aria-label={`New agent for ${signal}`} onClick={e => e.stopPropagation()}>
        <div className="sg-auto__head">
          <span className="sg-auto__bot"><SignalIcon id="robot" size={22} /></span>
          <div className="sg-grow">
            <h2>New agent for {signal}</h2>
            <p>Each time {signal} finds a move, it works through these steps in order.</p>
          </div>
          <button className="sg-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="sg-drawer__body sg-auto__body">
          <ol className="sg-auto__line">
            <li className="sg-auto__row">
              <span className={`sg-auto__dot sg-auto__dot--when sg-tchip__ic ${fam.key}`}><SignalIcon id={(row.signal || {}).signalType} size={16} /></span>
              <div className="sg-auto__card sg-auto__card--when">
                <span className="sg-auto__eyebrow">When</span>
                <b>{signal} finds a move</b>
                {row.scopeLabel && <span className="sg-auto__sub">Watching {row.scopeLabel}</span>}
              </div>
            </li>

            {steps.map((step, i) => {
              const st = STEPS[step.key];
              const removable = canRemove(i);
              return (
                <li key={step.id} className={`sg-auto__row ${drag === i ? "is-dragging" : ""}`}
                  draggable onDragStart={() => setDrag(i)} onDragEnd={() => setDrag(null)}
                  onDragOver={e => e.preventDefault()} onDrop={() => { if (drag != null) move_(drag, i); setDrag(null); }}>
                  <span className="sg-auto__dot">{i + 1}</span>
                  <div className="sg-auto__card">
                    <button className="sg-auto__grip" aria-label={`Move ${nameAt(step.key, i)}: drag, or use the arrow keys`}
                      onKeyDown={e => { if (e.key === "ArrowUp" || e.key === "ArrowDown") { e.preventDefault(); move_(i, i + (e.key === "ArrowUp" ? -1 : 1)); } }}>
                      <SignalIcon id="grip" size={16} strokeWidth={3} />
                    </button>
                    <span className={`sg-auto__tile is-${st.tone}`}><SignalIcon id={st.icon} size={18} /></span>
                    <div className="sg-auto__main">
                      <div className="sg-auto__name">
                        <b>{nameAt(step.key, i)}</b>
                        {st.price && <span className="sg-auto__tag">{st.price}</span>}
                        {st.ask && <span className="sg-auto__tag sg-auto__tag--ask">Asks you first</span>}
                      </div>
                      {pickFor(step, i)}
                      {step.key === TELL && slackOff && (
                        <span className="sg-auto__hint">
                          Slack isn't connected, so it emails you. <a className="sg-link" href={history.createHref({ pathname: "/settings/crm/slack" })} target="_blank" rel="noopener noreferrer">Connect Slack</a>
                        </span>
                      )}
                    </div>
                    <button className="sg-auto__x" aria-label={`Remove ${nameAt(step.key, i)}`} disabled={!removable}
                      title={removable ? "Remove step" : "A step below needs this one"} onClick={() => remove(i)}>×</button>
                  </div>
                </li>
              );
            })}

            <li className="sg-auto__row sg-auto__row--add">
              <span className="sg-auto__dot sg-auto__dot--add" aria-hidden="true">+</span>
              <div className="sg-auto__card sg-auto__card--add">
                <div className="sg-auto__addhead">
                  <b className="sg-grow">Then, add a step</b>
                  {!search && (
                    <button className="sg-auto__find" onClick={() => setSearch({ query: "", group: "all" })}>
                      <SignalIcon id="search" size={14} /> Search all steps
                    </button>
                  )}
                </div>

                {search ? (
                  <>
                    <div className="sg-auto__search">
                      <SignalIcon id="search" size={15} />
                      <input ref={searchRef} aria-label="Search all steps" placeholder={`Search or browse all ${offered.length} steps`}
                        value={search.query} onChange={e => setSearch({ ...search, query: e.target.value })} />
                      <button className="sg-link" onClick={() => setSearch(null)}>Clear</button>
                    </div>
                    <div className="sg-auto__groups" role="tablist">
                      {GROUPS.map(([g, label]) => (
                        <button key={g} role="tab" aria-selected={search.group === g} className={search.group === g ? "on" : ""} onClick={() => setSearch({ ...search, group: g })}>{label}</button>
                      ))}
                    </div>
                    <div className="sg-auto__all">
                      {GROUPS.slice(1).filter(([g]) => shownSearch.some(k => STEPS[k].group === g)).map(([g, label]) => (
                        <div key={g} className="sg-auto__grp">
                          <span className="sg-auto__eyebrow">{label}</span>
                          <div className="sg-auto__grid">
                            {shownSearch.filter(k => STEPS[k].group === g).map(k => {
                              const why = addReason(keys, k, entity, missing);
                              const editor = STEPS[k].editor;
                              return (
                                <button key={k} className="sg-auto__opt" disabled={!!why && !editor} onClick={() => (editor ? openEditor() : add(k))}>
                                  <span className={`sg-auto__tile sg-auto__tile--sm is-${STEPS[k].tone}`}><SignalIcon id={STEPS[k].icon} size={16} /></span>
                                  <span className="sg-auto__optxt"><b>{nameAt(k, insertAt(keys))}</b><span>{why || STEPS[k].note(ctx)}</span></span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      {!shownSearch.length && <span className="sg-auto__sub">No steps match “{search.query}”.</span>}
                    </div>
                  </>
                ) : compact ? (
                  <div className="sg-auto__chips">
                    {suggestions.map(k => (
                      <button key={k} className="sg-auto__chip" onClick={() => add(k)}>
                        <SignalIcon id={STEPS[k].icon} size={13} />{nameAt(k, insertAt(keys))}<span aria-hidden="true">+</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <>
                    {suggestions.length > 0 && <span className="sg-auto__sub">Suggested next for {stateAt(keys, keys.length, entity).people && entity === "company" ? "the people it found" : entity === "company" ? "a company" : move ? "a person who changed jobs" : "this person"}</span>}
                    <div className="sg-auto__recs">
                      {suggestions.map(k => (
                        <button key={k} className="sg-auto__rec" onClick={() => add(k)}>
                          <span className={`sg-auto__tile is-${STEPS[k].tone}`}><SignalIcon id={STEPS[k].icon} size={18} /></span>
                          <span className="sg-auto__optxt"><b>{nameAt(k, insertAt(keys))}</b><span>{STEPS[k].note(ctx)}</span></span>
                          <span className="sg-auto__addbtn">Add</span>
                        </button>
                      ))}
                    </div>
                    {locked && <div className="sg-auto__lock"><SignalIcon id="lock" size={14} /><span>{locked}</span></div>}
                  </>
                )}
              </div>
            </li>
          </ol>

          <div className="sg-auto__skips">
            <b>Skip rules</b>
            <label><input type="checkbox" checked={skipSeen} onChange={e => setSkipSeen(e.target.checked)} /><span>Skip {entity === "company" ? "companies" : "people"} this agent already handled</span></label>
            {keys.includes("act.enroll_sequence") && (
              <label><input type="checkbox" checked={skipInSequence} onChange={e => setSkipInSequence(e.target.checked)} /><span>Skip people already in a sequence</span></label>
            )}
            <span className="sg-auto__safe"><SignalIcon id="shield" size={14} />Do-not-contact and opted-out are always skipped.</span>
          </div>
        </div>

        <div className="sg-auto__foot">
          <b className={`sg-grow ${gap ? "sg-auto__gap" : ""}`}>{gap || costLine(steps, entity)}</b>
          <button className="sg-btn sg-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="sg-btn" disabled={busy || !!gap || !steps.length} onClick={turnOn}>{busy ? "Turning on…" : "Turn on"}</button>
        </div>
      </aside>
    </div>,
    document.body
  );
};

export const SignalAutomate = observer(SignalAutomateDrawer);

/** Draws the drawer wherever a Signals page mounts it; `onChange` runs after an agent is turned on or undone. */
export const AutomateHost = observer(({ onChange }) => {
  const open = automateState.open;
  // Leaving the page closes it, so it never reopens on the next visit.
  useEffect(() => () => { automateState.open = null; }, []);
  return open ? <SignalAutomate row={open.row} listId={open.listId} onChange={onChange} onClose={() => { automateState.open = null; }} /> : null;
});

export default SignalAutomate;
